<template>
    <div class="flex flex-col md:flex-row md:flex-wrap lg:flex-col lg:flex-nowrap w-full md:mb-8">
        <div class="detail-panel">
            <div class="detail-panel-content">
                <h4 v-text="t('show.sidebar.short_info')"></h4>
                <CompanyShowDetailItem
                    v-if="hasIndustries"
                    :label="t('show.sidebar.industry')"
                    icon="icon-company"
                >
                    <div class="flex flex-col justify-start">
                        <span
                            v-for="industry in cappedIndustries"
                            :key="industry.id"
                            v-text="industry.name"
                        ></span>
                        <span
                            v-if="company.industries.length > maxIndustries"
                            v-text="
                                t('show.sidebar.more_industries', {
                                    count: company.industries.length - maxIndustries,
                                })
                            "
                        >
                        </span>
                    </div>
                </CompanyShowDetailItem>
                <CompanyShowDetailItem
                    v-if="hasAbout && company.about.employees != null && company.about.employees.length > 0"
                    :label="t('show.sidebar.employees')"
                    icon="icon-users"
                >
                    {{ company.about.employees }}
                </CompanyShowDetailItem>
                <CompanyShowDetailItem
                    v-if="hasAbout"
                    :label="t('show.sidebar.address')"
                    icon="icon-location"
                >
                    <div class="flex flex-col justify-start">
                        <span
                            v-if="company.about.address != null && company.about.address.length > 0"
                            v-text="company.about.address"
                        ></span>
                        <div
                            v-if="hasLocations"
                            class="flex flex-col justify-start"
                        >
                            <label
                                class="text-sm mt-2"
                                v-text="t('show.sidebar.location')"
                            ></label>
                            <span
                                v-for="location in cappedLocations"
                                :key="location.id"
                                v-text="location.name"
                            ></span>
                            <span
                                v-if="company.locations.length > maxLocations"
                                v-tooltip="{ content: locationTooltip, html: true }"
                                v-text="
                                    t('show.sidebar.more_locations', {
                                        count: company.locations.length - maxLocations,
                                    })
                                "
                            ></span>
                        </div>
                    </div>
                </CompanyShowDetailItem>
                <CompanyShowDetailItem
                    v-if="hasAbout && company.about.website != null && company.about.website.length > 0"
                    :label="t('show.sidebar.website')"
                    icon="icon-web"
                >
                    <a
                        :href="company.about.website"
                        class="text-blue-600"
                        target="_blank"
                        :data-click="`short info box,${company.about.website_label}`"
                        v-text="company.about.website_label"
                    ></a>
                </CompanyShowDetailItem>
            </div>
        </div>
        <div
            v-if="hasAbout"
            class="detail-panel"
        >
            <div class="detail-panel-content">
                <h4 v-text="t('show.sidebar.contact')"></h4>
                <CompanyShowDetailItem
                    v-if="hasAbout && company.about.hr_contact != null && company.about.hr_contact.length > 0"
                    :label="t('show.sidebar.contact_person')"
                    icon="icon-profile"
                >
                    {{ company.about.hr_contact }}
                </CompanyShowDetailItem>
                <CompanyShowDetailItem
                    v-if="hasAbout && company.about.phone != null && company.about.phone.length > 0"
                    :label="t('show.sidebar.phone')"
                    icon="icon-telephone"
                >
                    {{ company.about.phone }}
                </CompanyShowDetailItem>
                <CompanyShowDetailItem
                    v-if="hasAbout && company.about.email != null && company.about.email.length > 0"
                    :label="t('show.sidebar.email')"
                    icon="icon-mail"
                >
                    <a
                        :href="`mailto:${company.about.email}`"
                        class="text-blue-600"
                        target="_blank"
                        :data-click="`contact box,${company.about.email}`"
                        >{{ company.about.email }}</a
                    >
                </CompanyShowDetailItem>
            </div>
        </div>
        <div
            v-if="hasSocialNetworks"
            class="detail-panel"
        >
            <div class="detail-panel-content">
                <h4 v-text="t('show.sidebar.social_media')"></h4>
                <a
                    v-for="(link, name) in validSocialNetworks"
                    :key="name"
                    :href="link"
                    target="_blank"
                    class="social-network-link flex flex-row w-full items-center text-blue-600"
                >
                    <i :class="`w-5 h-5 icon text-slate-400 ${t('show.sidebar.social_network.' + name + '.icon')}`"></i
                    ><span
                        class="text-base ml-3"
                        :data-click="`social media box,${name}`"
                        v-text="t('show.sidebar.social_network.' + name + '.label')"
                    ></span>
                </a>
            </div>
        </div>
        <div
            v-if="hasFacebookIntegration && company.social_networks_integrations && isFacebookPluginEnabled"
            class="facebook-widget-container"
        >
            <div
                :data-href="`https://www.facebook.com/${company.social_networks_integrations.facebook.id}`"
                class="fb-page with-shadow flex flex-row items-center justify-center"
                data-tabs="timeline"
                data-height="350"
                data-width=""
                data-small-header="true"
                data-adapt-container-width="true"
                data-hide-cover="false"
                data-show-facepile="false"
            >
                <blockquote
                    :cite="`https://www.facebook.com/${company.social_networks_integrations.facebook.id}`"
                    class="fb-xfbml-parse-ignore"
                >
                    <a
                        :href="`https://www.facebook.com/${company.social_networks_integrations.facebook.id}`"
                        v-text="company.name"
                    ></a>
                </blockquote>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { pickBy, reduce, slice } from 'lodash-es';
import { useTranslation } from 'i18next-vue';
import CompanyShowDetailItem from './CompanyShowDetailItem.vue';

export default {
    name: 'CompanyShowDetails',
    components: { CompanyShowDetailItem },
    props: {
        isFacebookPluginEnabled: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const { t } = useTranslation('company');
        return { t };
    },
    data() {
        return {
            maxIndustries: 5,
            maxLocations: 5,
        };
    },
    computed: {
        ...mapGetters({
            company: 'company/currentCompany',
            screenSize: 'ui/screenSize',
        }),
        hasAbout() {
            return this.company && this.company.about;
        },
        hasLocations() {
            return this.company && this.company.locations && this.company.locations.length > 0;
        },
        cappedLocations() {
            if (this.hasLocations) {
                return this.company.locations.slice(0, this.maxLocations);
            }
            return [];
        },
        hasIndustries() {
            return this.company && this.company.industries && this.company.industries.length > 0;
        },
        validSocialNetworks() {
            if (this.company && this.company.social_networks) {
                return pickBy(this.company.social_networks, (item) => item !== null && item.length > 0);
            }
            return [];
        },
        hasSocialNetworks() {
            return this.validSocialNetworks && Object.keys(this.validSocialNetworks).length > 0;
        },
        hasFacebookIntegration() {
            return true;
        },
        cappedIndustries() {
            if (this.hasIndustries) {
                return this.company.industries.slice(0, this.maxIndustries);
            }
            return [];
        },
        locationTooltip() {
            const locations = slice(this.company.locations, this.maxLocations);

            return reduce(
                locations,
                (val, location) => {
                    return `${val}<div>${location.name}</div>`;
                },
                '',
            );
        },
    },
    watch: {
        screenSize(newVal, oldVal) {
            if (newVal && oldVal) {
                this.$forceUpdate();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.masonry {
    @screen md {
        // this forces a "masonry like" for MD screens
        @apply flex flex-wrap flex-row w-full items-stretch;
        max-height: 1300px;
    }
    @screen lg {
        @apply hidden;
    }
}
.detail-panel {
    @apply mb-6;
    @screen md {
        @apply w-1/2 mb-8;
        &:nth-child(1) {
            @apply pr-4;
        }
        &:nth-child(2) {
            @apply pl-4;
        }
        &:nth-child(3) {
            @apply pr-4;
        }
    }

    @screen lg {
        @apply w-full;
        &:nth-child(even) {
            @apply pl-0;
        }
        &:nth-child(odd) {
            @apply pr-0;
        }
    }
    .detail-panel-content {
        @apply pt-5 pb-6 px-4 bg-slate-50 rounded-lg w-full h-full shadow;
        h4 {
            @apply text-black  mb-6;
        }
        .detail-item:not(:last-child) {
            @apply mb-4;
        }
        .social-network-link:not(:last-child) {
            @apply mb-3;
        }
    }
}
.facebook-widget-container {
    @apply mb-8 w-full mx-auto flex items-center justify-center;

    .fb-page :deep(iframe) {
        @apply rounded-lg shadow;
        overflow: hidden;
    }

    @screen md {
        @apply w-1/2 pt-0 pl-4 pr-0 text-center;
        .fb-page,
        .fb-page span,
        .fb-page span iframe[style] {
            width: 100% !important;
        }
    }
    @screen lg {
        @apply w-full p-0;
    }
}
</style>
